import { Button, Flex, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteWidgetSettingsWidgetSettingsDeleteDeleteMutation } from '@shared/api';
import { useNotify } from '@shared/hooks/use-notify';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { PageHeader } from '@shared/ui/page-header';
import { useGetPresets } from './hooks/use-get-presets';
import { PresetsEmptyState } from './ui/empty-state/presets-empty-state';
import { PresetsTable } from './ui/presets-table/presets-table';
import styles from './presets.module.scss';

export const Presets = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets' });
  const { Text } = Typography;
  const navigate = useNavigate();
  const notify = useNotify();
  const { openConfirmModal } = useGlobalConfirmModalActions();
  const { presets, isLoading, isFetching, isError, refetch } = useGetPresets();
  const [deletePreset, { isLoading: isDeleting }] =
    useDeleteWidgetSettingsWidgetSettingsDeleteDeleteMutation();
  const isPending = isLoading || isFetching;
  const hasPresets = Boolean(presets?.length);
  const handleCreatePreset = () => navigate('create');
  const handleEditPreset = (presetId: number) => navigate(String(presetId));

  const handleDeletePreset = async (presetId: number) => {
    try {
      await deletePreset({ widgetSettingsId: presetId }).unwrap();
      notify({ type: 'success', message: t('notifications.deleted') });
    } catch {
      notify({ message: t('notifications.deleteError') });
      throw new Error('Failed to delete preset');
    }
  };

  const handleOpenDeleteConfirm = (presetId: number) => {
    const preset = presets.find((item) => item.id === presetId);

    openConfirmModal({
      title: t('deleteModal.title'),
      text: (
        <Trans
          t={t}
          i18nKey="deleteModal.text"
          values={{ preset: preset?.name ?? '-' }}
          components={{ bold: <Text strong /> }}
        />
      ),
      confirmButtonLabel: t('deleteModal.confirm'),
      cancelButtonLabel: t('deleteModal.cancel'),
      onConfirm: () => handleDeletePreset(presetId),
    });
  };

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader title={t('title')} />

      <Flex vertical gap={16} className={styles.container}>
        {isPending && <LoadingState />}
        {!isPending && isError && <FetchingErrorState refetch={refetch} />}

        {!isPending && !isError && hasPresets && (
          <>
            <div className={styles.tableWrapper}>
              <PresetsTable
                presets={presets}
                onEdit={handleEditPreset}
                onDelete={handleOpenDeleteConfirm}
                isDeleting={isDeleting}
              />
            </div>

            <Flex justify="flex-end">
              <Button type="primary" size="large" onClick={handleCreatePreset}>
                {t('actions.create')}
              </Button>
            </Flex>
          </>
        )}

        {!isPending && !isError && !hasPresets && (
          <PresetsEmptyState onCreate={handleCreatePreset} />
        )}
      </Flex>
    </Flex>
  );
};

export { PresetEditor } from './ui/preset-editor/preset-editor';
